import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useSystemTimezone } from '@/hooks/use-system-timezone';
import {
  User,
  Building2,
  Calendar,
  DollarSign,
  CreditCard,
  FileText,
  Receipt,
  Store,
  MessageSquare,
  Check,
  X,
  Clock,
  Trash2,
} from 'lucide-react';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface ExpenseViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense: any;
  onDelete?: (expense: any) => void;
  isAdmin?: boolean;
}

export default function ExpenseViewDialog({
  open,
  onOpenChange,
  expense,
  onDelete,
  isAdmin = false,
}: ExpenseViewDialogProps) {
  const systemTimezone = useSystemTimezone();

  if (!expense) return null;

  const getStatusBadge = () => {
    if (expense.is_approved === null) {
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    } else if (expense.is_approved === true || expense.is_approved === 1) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          <Check className="h-3 w-3 mr-1" />
          Approved
        </Badge>
      );
    } else {
      return (
        <Badge variant="destructive" className="bg-red-100 text-red-800">
          <X className="h-3 w-3 mr-1" />
          Rejected
        </Badge>
      );
    }
  };

  const getTypeBadge = () => {
    const colors = {
      company: 'bg-blue-100 text-blue-800',
      personal: 'bg-purple-100 text-purple-800',
      reimbursement: 'bg-orange-100 text-orange-800'
    };
    return (
      <Badge variant="outline" className={colors[expense.expense_type as keyof typeof colors]}>
        {expense.expense_type.charAt(0).toUpperCase() + expense.expense_type.slice(1)}
      </Badge>
    );
  };

  const formatPaymentMethod = (method: string) => {
    return method.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Expense Details</span>
            {getStatusBadge()}
          </DialogTitle>
          <DialogDescription>
            Complete information about this expense record
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Title</label>
                  <p className="font-medium">{expense.title}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Category</label>
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: expense.category_color }}
                    />
                    <span className="font-medium">{expense.category_name}</span>
                  </div>
                </div>
              </div>
              
              {expense.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="text-sm">{expense.description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Amount</label>
                  <p className="text-2xl font-bold text-green-600">{(() => {
                    const currencySymbol = useCurrencySymbol();
                    return `${currencySymbol}${(Number(expense.amount) || 0).toFixed(2)}`;
                  })()}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Type</label>
                  <div className="mt-1">{getTypeBadge()}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Date</label>
                  <p className="font-medium flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {format(new Date(expense.expense_date), 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Payment Method</label>
                  <p className="font-medium">{formatPaymentMethod(expense.payment_method)}</p>
                </div>
                {expense.vendor_name && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Vendor/Supplier</label>
                    <p className="font-medium flex items-center">
                      <Store className="h-4 w-4 mr-1" />
                      {expense.vendor_name}
                    </p>
                  </div>
                )}
              </div>
              
              {expense.receipt_number && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Receipt Number</label>
                  <p className="font-medium flex items-center">
                    <Receipt className="h-4 w-4 mr-1" />
                    {expense.receipt_number}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* User & Branch Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <User className="h-5 w-5 mr-2" />
                Submitted By
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Employee</label>
                  <p className="font-medium">{expense.user_name}</p>
                </div>
                {expense.branch_name && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Branch</label>
                    <p className="font-medium flex items-center">
                      <Building2 className="h-4 w-4 mr-1" />
                      {expense.branch_name}
                    </p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Submitted On</label>
                <p className="text-sm">{format(new Date(expense.created_at), 'MMM dd, yyyy HH:mm')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Approval Information */}
          {expense.is_approved !== null && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  {(expense.is_approved === true || expense.is_approved === 1) ? (
                    <Check className="h-5 w-5 mr-2 text-green-600" />
                  ) : (
                    <X className="h-5 w-5 mr-2 text-red-600" />
                  )}
                  Approval Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {(expense.is_approved === true || expense.is_approved === 1) ? 'Approved By' : 'Rejected By'}
                    </label>
                    <p className="font-medium">{expense.approved_by_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Date</label>
                    <p className="font-medium">
                      {formatInTimeZone(new Date(`${expense.approved_at}Z`), systemTimezone, 'MMM dd, yyyy HH:mm')}
                    </p>
                  </div>
                </div>
                
                {expense.rejection_reason && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Rejection Reason</label>
                    <p className="text-sm bg-red-50 p-3 rounded-md border-l-4 border-red-400">
                      {expense.rejection_reason}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {expense.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm bg-gray-50 p-3 rounded-md">{expense.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Attachment placeholder */}
          {expense.attachment_path && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Receipt className="h-5 w-5 mr-2" />
                  Attachment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm font-medium">Receipt/Invoice</span>
                  <Button variant="outline" size="sm">
                    View Attachment
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex-1">
            {onDelete && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  // Check if admin can delete or if it's a pending expense
                  const canDelete = isAdmin || expense.is_approved === null;
                  if (!canDelete && (expense.is_approved === true || expense.is_approved === 1)) {
                    // This shouldn't happen as button should be disabled, but just in case
                    return;
                  }
                  onDelete(expense);
                  onOpenChange(false);
                }}
                disabled={!isAdmin && (expense.is_approved === true || expense.is_approved === 1)}
                className="mr-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Expense
              </Button>
            )}
          </div>
          <Button onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}